import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Form, notification, Spin } from 'antd'
import { useAtomValue } from 'jotai'

import { PageHeader } from '@/components/ui/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { ConfirmModal } from '@/components/ui/confirm-modal'
import {
    createMail,
    encryptMailPassword,
    fetchMails,
    getMailById,
    testMailConnection,
    updateMail,
} from '@/api/mails.api'
import type { MailWriteInput } from '@/api/mails.api'
import { currentUserAtom } from '@/store/auth.atom'

const PROTOCOL_OPTIONS = [
    { value: 1, label: 'STARTTLS' },
    { value: 2, label: 'SSL/TLS' },
]

const PILL_INPUT_CLASS ='!bg-white !text-[#1E2F41] disabled:!bg-gray-100 disabled:hover:!bg-gray-100 disabled:!text-gray-500 disabled:!cursor-not-allowed'

export function MailServerConfigPage() {
    const currentUser = useAtomValue(currentUserAtom)
    const queryClient = useQueryClient()
    const [form] = Form.useForm()

    const [isEditing, setIsEditing] = useState(false)
    const [isTesting, setIsTesting] = useState(false)
    const [testResult, setTestResult] = useState<{
        status: 'success' | 'error'
        host?: string
        message?: string
    } | null>(null)

    const { data: mail, isLoading } = useQuery({
        queryKey: ['mail-single-config', currentUser?.unitId],
        queryFn: async ({ signal }) => {
            const res = await fetchMails(0, 1, { unitId: currentUser?.unitId }, signal)
            if (res.items.length > 0) {
                return getMailById(res.items[0].id, signal)
            }
            return null
        },
        enabled: !!currentUser?.unitId,
        refetchOnMount: 'always',
        staleTime: 0,
        gcTime: 0,
    })

    useEffect(() => {
        if (mail) {
            form.setFieldsValue({
                serverUrl: mail.serverUrl,
                serverPort: mail.serverPort,
                protocol: mail.protocol,
                mail: mail.mail,
                name: mail.name,
                username: mail.username,
                password: mail.password ? '******' : '',
            })
        } else {
            form.resetFields()
        }
    }, [mail, form, isEditing])

    const saveMutation = useMutation({
        mutationFn: async (values: any) => {
            let finalPassword = values.password
            if (mail && (!finalPassword || finalPassword === '******')) {
                finalPassword = mail.password
            } else if (finalPassword) {
                finalPassword = await encryptMailPassword(finalPassword)
            }

            const payload: MailWriteInput = {
                unitId: currentUser?.unitId || '',
                serverUrl: values.serverUrl,
                serverPort: values.serverPort,
                protocol: values.protocol,
                mail: values.mail,
                name: values.name,
                username: values.username,
                ...(finalPassword ? { password: finalPassword } : {}),
                ...(mail ? { status: mail.status } : { status: 1 }),
            }

            return mail ? updateMail(mail.id, payload) : createMail(payload)
        },
        onSuccess: (res: any) => {
            notification.success({
                message: res.responseMessage || 'Lưu cấu hình mail server thành công!',
            })
            queryClient.invalidateQueries({ queryKey: ['mail-single-config'] })
            setIsEditing(false)
        },
        onError: (error: any) => {
            notification.error({
                message: error?.message || 'Đã xảy ra lỗi khi lưu cấu hình.',
            })
        },
    })

    const handleTestConnection = async () => {
        try {
            setIsTesting(true)
            const values = await form.validateFields()
            let testPassword = values.password

            if (mail && (!testPassword || testPassword === '******')) {
                testPassword = mail.password
            } else if (testPassword) {
                testPassword = await encryptMailPassword(testPassword)
            }

            await testMailConnection({
                serverUrl: values.serverUrl,
                serverPort: values.serverPort,
                protocol: values.protocol,
                username: values.username,
                password: testPassword,
            })

            setTestResult({ status: 'success', host: values.serverUrl })
        } catch (error: any) {
            if (error?.errorFields) return
            setTestResult({
                status: 'error',
                message: error?.message || 'Lỗi do hệ thống trả về.',
            })
        } finally {
            setIsTesting(false)
        }
    }

    const handleCancel = () => {
        form.resetFields()
        if (mail) {
            form.setFieldsValue({
                serverUrl: mail.serverUrl,
                serverPort: mail.serverPort,
                protocol: mail.protocol,
                mail: mail.mail,
                name: mail.name,
                username: mail.username,
                password: mail.password,
            })
        }
        setIsEditing(false)
    }

    return (
        <section className="space-y-4">
            <PageHeader title="DANH SÁCH MAIL">
                {!isEditing && (
                    <Button
                        variant="primary"
                        size="md"
                        className="flex-1 gap-2 h-10 min-w-[150px]"
                        onClick={() => setIsEditing(true)}
                    >
                        Cập nhật
                    </Button>
                )}
            </PageHeader>

            <div className="rounded-2xl border border-[#E5E7EB] bg-white p-8 shadow-sm">
                <Spin spinning={isLoading}>
                    <Form
                        form={form}
                        layout="vertical"
                        disabled={!isEditing}
                        onFinish={(values) => saveMutation.mutate(values)}
                        requiredMark={(label, info) => (
                            <span className="text-sm font-medium text-[#1E2F41]">
                                {label}
                                {info.required && <span className="ml-1 text-red-500">*</span>}
                            </span>
                        )}
                        className="space-y-5"
                    >
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
                            <Form.Item
                                name="serverUrl"
                                label="Máy chủ SMTP (Host/IP)"
                                rules={[{ required: true, message: 'Vui lòng nhập máy chủ SMTP' }]}
                                className="md:col-span-4 !mb-0"
                            >
                                <Input
                                    size="large"
                                    placeholder="Nhập host/IP"
                                    className={PILL_INPUT_CLASS}
                                />
                            </Form.Item>

                            <Form.Item
                                name="serverPort"
                                label="Cổng"
                                rules={[{ required: true, message: 'Vui lòng nhập cổng' }]}
                                className="md:col-span-2 !mb-0"
                            >
                                <Input
                                    type="number"
                                    size="large"
                                    placeholder="Ví dụ: 587"
                                    className={PILL_INPUT_CLASS}
                                />
                            </Form.Item>

                            <Form.Item
                                name="protocol"
                                label="Giao thức"
                                rules={[{ required: true, message: 'Vui lòng chọn giao thức' }]}
                                className="md:col-span-6 !mb-0"
                            >
                                <Select
                                    size="large"
                                    placeholder="Chọn giao thức"
                                    className="w-full !rounded-full"
                                    options={PROTOCOL_OPTIONS}
                                    value={form.getFieldValue('protocol')}
                                    onValueChange={(val) => {
                                        form.setFieldsValue({ protocol: val })
                                        if (val === 1) form.setFieldsValue({ serverPort: 587 })
                                        if (val === 2) form.setFieldsValue({ serverPort: 465 })
                                    }}
                                    disabled={!isEditing}
                                />
                            </Form.Item>
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <Form.Item
                                name="mail"
                                label="Địa chỉ mail"
                                rules={[
                                    { required: true, message: 'Vui lòng nhập địa chỉ mail' },
                                    { type: 'email', message: 'Địa chỉ mail không hợp lệ' },
                                ]}
                                className="!mb-0"
                            >
                                <Input
                                    size="large"
                                        placeholder="Ví dụ: support@robot.com"
                                    className={PILL_INPUT_CLASS}
                                />
                            </Form.Item>

                            <Form.Item
                                name="name"
                                label="Tên hiển thị"
                                rules={[{ required: true, message: 'Vui lòng nhập tên hiển thị' }]}
                                className="!mb-0"
                            >
                                <Input
                                    size="large"
                                    placeholder="Ví dụ: Hệ thống quản trị robot"
                                    className={PILL_INPUT_CLASS}
                                />
                            </Form.Item>
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <Form.Item
                                name="username"
                                label="Tên đăng nhập"
                                rules={[{ required: true, message: 'Vui lòng nhập tên đăng nhập' }]}
                                className="!mb-0"
                            >
                                <Input
                                    size="large"
                                    placeholder="Ví dụ: support@robot.com"
                                    className={PILL_INPUT_CLASS}
                                />
                            </Form.Item>

                            <Form.Item
                                name="password"
                                label="Mật khẩu"
                                rules={[{ required: true, message: 'Vui lòng nhập mật khẩu' }]}
                                className="!mb-0"
                            >
                                <Input
                                    type="password"
                                    size="large"
                                    placeholder={mail ? '••••••' : 'Nhập mật khẩu'}
                                    className={PILL_INPUT_CLASS}
                                />
                            </Form.Item>
                        </div>
                    </Form>
                </Spin>
            </div>

            {isEditing && (
                <div className="flex justify-end gap-3 pt-2">
                    <Button
                        variant="outline"
                        size="md"
                        onClick={handleCancel}
                        disabled={saveMutation.isPending || isTesting}
                        className="min-w-[150px] h-10"
                    >
                        Hủy
                    </Button>
                    <Button
                        variant="outline"
                        size="md"
                        onClick={handleTestConnection}
                        disabled={saveMutation.isPending || isTesting}
                        className="min-w-[150px] h-10 border-[#CE7A58] text-[#CE7A58] hover:bg-[#CE7A58]/10"
                    >
                        Kiểm tra kết nối
                    </Button>
                    <Button
                        variant="primary"
                        size="md"
                        onClick={() => form.submit()}
                        disabled={saveMutation.isPending || isTesting}
                        className="h-10 min-w-[150px]"
                    >
                        {saveMutation.isPending ? 'Đang lưu...' : 'Lưu'}
                    </Button>
                </div>
            )}

            <ConfirmModal
                isOpen={testResult?.status === 'success'}
                title="Kết nối thành công"
                description={`Kết nối đến Mail Server {${testResult?.host || ''}} thành công.`}
                variant="success"
                onClose={() => setTestResult(null)}
                cancelText="Đóng"
            />

            <ConfirmModal
                isOpen={testResult?.status === 'error'}
                title="Kết nối thất bại"
                description={testResult?.message || 'Lỗi do hệ thống trả về'}
                variant="danger"
                onClose={() => setTestResult(null)}
                cancelText="Đóng"
            />
        </section>
    )
}